import React, { useState, useContext } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { AppContext } from '../contexts/AppContext';
import { Rol } from '../types';

const MemberLoginScreen: React.FC = () => {
  const ui = useUI();
  const { setUnauthenticatedView } = useContext(AppContext);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmailExists = async (emailToCheck: string) => {
    try {
      // Llamamos a la Edge Function `check-socio-exists` que usa la service_role_key
      // internamente para evitar problemas de RLS. En el cliente usamos la ANON key
      // como Authorization header para invocar la función.
      const normalized = String(emailToCheck).trim().toLowerCase();
      // Construimos el host de funciones a partir de la URL del proyecto
      const projectHost = SUPABASE_URL.replace(/^https?:\/\//, '').replace('.supabase.co', '');
      const fnUrl = `https://${projectHost}.functions.supabase.co/check-socio-exists`;

      console.log('validateEmailExists -> calling function', fnUrl, 'queried:', normalized);

      const resp = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ email: normalized })
      });

      if (!resp.ok) {
        console.warn('check-socio-exists returned non-ok:', resp.status, await resp.text());
        return false;
      }

      const json = await resp.json();
      console.log('validateEmailExists -> function response:', json);
      return !!json?.exists;

      // No intentamos consultar `profiles.email` porque en este esquema los socios
      // pueden no tener fila en `profiles` y la columna `email` podría no existir.
      // Por ahora solo validamos contra `socios` para evitar el error 42703 y
      // peticiones fallidas en la consola del navegador.
      return false;
    } catch (err) {
      console.warn('Validación de email fallo:', err);
      return false;
    }
  };

  const handleSendMagicLink = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email) {
      ui.showToast('error', 'Ingresá un email válido.');
      return;
    }

    setLoading(true);
    try {
      const exists = await validateEmailExists(email.trim());
      if (!exists) {
        ui.showToast('error', 'No encontramos una cuenta de socio asociada a ese email.');
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { emailRedirectTo: window.location.origin } });
      if (error) {
        ui.showToast('error', `No se pudo enviar el link: ${error.message || String(error)}`);
      } else {
        ui.showToast('success', 'Link de acceso enviado. Revisá tu email.');
      }
    } catch (err: any) {
      ui.showToast('error', `Error: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4">
      <div className="text-center mb-8">
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); setUnauthenticatedView('landing'); }}
          className="inline-block"
        >
          <img src="/logo.png" alt="Tu Club" className="h-16 mx-auto mb-4" />
        </a>
        <p className="text-gray-500">Ingreso para Socios — Accedé con tu email</p>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-sm">
        <form onSubmit={handleSendMagicLink} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <button type="submit" disabled={loading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
              {loading ? 'Enviando link...' : 'Enviar enlace de acceso'}
            </button>
          </div>

          <div className="mt-4 text-center text-sm text-gray-500">
            <a href="#" onClick={(e) => { e.preventDefault(); setUnauthenticatedView('login'); }} className="text-blue-600 hover:underline">¿Sos administrador? Ingresá aquí</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberLoginScreen;
